import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { useEffect, useMemo, useState } from "react";
import { fetchQuestionnaireStructure } from "../../Operator/OperatorAPI/OperatorGet";
import {
  postBulkChangeTime,
  postBulkSendMessage,
  postBulkSendPopup,
  postBulkMarkAllRead,
  postBulkSetOverrideNext,
  postBulkSetPaused,
  postBulkSkip
} from "../../Operator/OperatorAPI/OperatorPost";

/** Summary of which users a bulk operation changed and which it skipped, and why. */
const BulkResultPanel = ({ result }) => {
  if (!result) return null;

  const { succeeded = [], skipped = [] } = result;
  const severity = succeeded.length === 0 ? "warning" : skipped.length > 0 ? "info" : "success";

  return (
    <Alert severity={severity} sx={{ mt: 2 }} dir="rtl">
      <AlertTitle>
        הפעולה בוצעה עבור {succeeded.length} נבחנים
        {skipped.length > 0 && `, ${skipped.length} דולגו`}
      </AlertTitle>
      {skipped.length > 0 && (
        <Accordion elevation={0} sx={{ bgcolor: "transparent", "&:before": { display: "none" } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, minHeight: 32 }}>
            <Typography variant="body2">הצג את מי שדולג</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0, pt: 0, maxHeight: 220, overflowY: "auto" }}>
            <Stack spacing={0.5}>
              {skipped.map(user => (
                <Typography key={user.id} variant="caption">
                  <b>{user.name}</b> — {user.reason}
                </Typography>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}
    </Alert>
  );
};

/** Move every targeted user to the start of a chosen chapter, or to the end of the current one. */
const MovePanel = ({ targetUsers, run }) => {
  // Users can sit on different questionnaires, and a chapter only exists inside one of
  // them — so the operator picks which questionnaire the move applies to.
  const questionnaires = useMemo(() => {
    const names = targetUsers.map(u => u.questionnaire).filter(Boolean);
    return [...new Set(names)];
  }, [targetUsers]);

  const [questionnaireName, setQuestionnaireName] = useState(questionnaires[0] || "");
  const [structure, setStructure] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [expanded, setExpanded] = useState(0);
  // Move now by default; unchecking falls back to waiting for each test-taker's "next".
  const [immediate, setImmediate] = useState(true);

  useEffect(() => {
    if (questionnaires.length === 0) return;
    if (!questionnaires.includes(questionnaireName)) setQuestionnaireName(questionnaires[0]);
  }, [questionnaires, questionnaireName]);

  useEffect(() => {
    if (!questionnaireName) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    fetchQuestionnaireStructure(questionnaireName)
      .then(data => {
        if (!cancelled) setStructure(data);
      })
      .catch(() => {
        if (!cancelled) setLoadError("טעינת מבנה השאלון נכשלה");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [questionnaireName]);

  const chapterFirstPos = (testName, chapter) =>
    chapter.hasReferencesPage
      ? { state: "REFERENCES", testId: testName, chapterId: chapter.chapterName, questionIndex: null }
      : { state: "QUESTION", testId: testName, chapterId: chapter.chapterName, questionIndex: 0 };

  const matchingUsers = targetUsers.filter(u => u.questionnaire === questionnaireName);

  return (
    <Box dir="rtl">
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        ההזזה חלה רק על נבחנים בשאלון הנבחר.
      </Typography>
      <FormControlLabel
        sx={{ mb: 1 }}
        control={
          <Checkbox size="small" checked={immediate} onChange={e => setImmediate(e.target.checked)} />
        }
        label={
          <Typography variant="caption" color="text.secondary">
            {immediate
              ? "העבר מיד — כל נבחן יקבל הודעה ויועבר בלי להמתין"
              : 'המתן — כל נבחן יועבר כשילחץ על "הבא"'}
          </Typography>
        }
      />

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>שאלון</InputLabel>
          <Select
            value={questionnaireName}
            label="שאלון"
            onChange={e => setQuestionnaireName(e.target.value)}
          >
            {questionnaires.map(name => (
              <MenuItem key={name} value={name}>{name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Chip label={`${matchingUsers.length} נבחנים בשאלון זה`} size="small" />
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Button
          size="small"
          variant="outlined"
          onClick={() => run("דילוג לסוף הפרק הנוכחי", userIDs => postBulkSkip("chapter", userIDs, immediate))}
        >
          ⏭ דלג לסוף הפרק הנוכחי
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() => run("דילוג לסוף המבחן הנוכחי", userIDs => postBulkSkip("test", userIDs, immediate))}
        >
          ⏭ דלג לסוף המבחן הנוכחי
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        הדילוגים למעלה חלים על כל הנבחנים בהיקף שנבחר, כל אחד ביחס למיקומו הנוכחי.
      </Typography>

      <Divider sx={{ my: 2 }} />

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          <CircularProgress />
        </Box>
      )}
      {loadError && <Alert severity="error">{loadError}</Alert>}

      {structure && structure.tests.map((test, tIdx) => (
        <Accordion
          key={test.testName}
          expanded={expanded === tIdx}
          onChange={() => setExpanded(expanded === tIdx ? null : tIdx)}
          disableGutters
          elevation={0}
          sx={{ borderBottom: "1px solid", borderColor: "divider", "&:before": { display: "none" } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1, minHeight: 44 }}>
            <Typography sx={{ flex: 1 }}>{test.testName}</Typography>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              sx={{ ml: 1, whiteSpace: "nowrap", flexShrink: 0 }}
              onClick={e => {
                e.stopPropagation();
                const firstChapter = test.chapters[0];
                if (!firstChapter) return;
                run(
                  `הזזת כל הנבחנים בשאלון "${questionnaireName}" לתחילת "${test.testName}"`,
                  userIDs => postBulkSetOverrideNext(chapterFirstPos(test.testName, firstChapter), questionnaireName, userIDs, immediate),
                  matchingUsers.length
                );
              }}
            >
              תחילת שאלון
            </Button>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 1, py: 1 }}>
            <Stack spacing={0.5}>
              {test.chapters.map((chapter, cIdx) => (
                <Box
                  key={chapter.chapterName}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1,
                    bgcolor: cIdx % 2 === 0 ? "grey.50" : "background.paper"
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2">{chapter.chapterName}</Typography>
                    {chapter.hasReferencesPage && (
                      <Chip label="הפניות" size="small" sx={{ height: 16, fontSize: "0.65rem" }} />
                    )}
                  </Stack>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() =>
                      run(
                        `הזזת כל הנבחנים בשאלון "${questionnaireName}" לפרק "${chapter.chapterName}" (${test.testName})`,
                        userIDs => postBulkSetOverrideNext(chapterFirstPos(test.testName, chapter), questionnaireName, userIDs, immediate),
                        matchingUsers.length
                      )
                    }
                  >
                    הזז את כולם לכאן
                  </Button>
                </Box>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

/** Add or remove time from every targeted user's chapter or references timer. */
const TimePanel = ({ run }) => {
  const [minutes, setMinutes] = useState(5);
  const [section, setSection] = useState("chapter");

  const sectionLabel = section === "chapter" ? "הפרק" : "דף ההפניות";
  const valid = Number.isFinite(minutes) && minutes > 0;

  return (
    <Box dir="rtl">
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        השינוי חל רק על נבחנים שנמצאים כרגע ב{sectionLabel}. מי שלא — ידולג, ותקבל/י רשימה עם הסיבה.
      </Typography>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <TextField
          label="דקות"
          type="number"
          size="small"
          value={minutes}
          onChange={e => setMinutes(Number(e.target.value))}
          inputProps={{ min: 1 }}
          sx={{ width: 120 }}
        />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={section}
          onChange={(e, value) => value && setSection(value)}
        >
          <ToggleButton value="chapter">פרק</ToggleButton>
          <ToggleButton value="references">דף הפניות</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          color="success"
          disabled={!valid}
          onClick={() =>
            run(`הוספת ${minutes} דקות ל${sectionLabel} של כל הנבחנים`, userIDs =>
              postBulkChangeTime(section, minutes, true, userIDs)
            )
          }
        >
          ➕ הוסף זמן לכולם
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={!valid}
          onClick={() =>
            run(`הפחתת ${minutes} דקות מ${sectionLabel} של כל הנבחנים`, userIDs =>
              postBulkChangeTime(section, minutes, false, userIDs)
            )
          }
        >
          ➖ הפחת זמן לכולם
        </Button>
      </Stack>
    </Box>
  );
};

/** Send a popup or a regular operator message to every targeted user, or clear their highlights. */
const MessagesPanel = ({ targetUsers, run }) => {
  const [popupMessage, setPopupMessage] = useState("");
  const [message, setMessage] = useState("");

  const pendingIssues = targetUsers.filter(u => u.raise_hand).length;

  return (
    <Box dir="rtl">
      <Typography variant="subtitle2" sx={{ mb: 1 }}>חלון קופץ (פופאפ)</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        מופיע מיד על מסך הנבחן ואינו עוצר את המבחן.
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
        <TextField
          size="small"
          fullWidth
          multiline
          maxRows={4}
          placeholder="תוכן הפופאפ..."
          value={popupMessage}
          onChange={e => setPopupMessage(e.target.value)}
        />
        <Button
          variant="contained"
          color="warning"
          disabled={!popupMessage.trim()}
          sx={{ whiteSpace: "nowrap" }}
          onClick={() =>
            run(`שליחת פופאפ לכל הנבחנים: "${popupMessage}"`, userIDs => postBulkSendPopup(popupMessage, userIDs))
          }
        >
          שלח פופאפ
        </Button>
      </Stack>

      <Divider sx={{ mb: 2 }} />

      <Typography variant="subtitle2" sx={{ mb: 1 }}>הודעה לתיבת ההודעות</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        נשמרת אצל הנבחן ונקראת דרך כפתור דיווח התקלות.
      </Typography>
      <Stack direction="row" spacing={1}>
        <TextField
          size="small"
          fullWidth
          multiline
          maxRows={4}
          placeholder="תוכן ההודעה..."
          value={message}
          onChange={e => setMessage(e.target.value)}
        />
        <Button
          variant="contained"
          disabled={!message.trim()}
          sx={{ whiteSpace: "nowrap" }}
          onClick={() => run(`שליחת הודעה לכל הנבחנים: "${message}"`, userIDs => postBulkSendMessage(message, userIDs))}
        >
          שלח הודעה
        </Button>
      </Stack>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ mb: 1 }}>סימון פניות כנקראו</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        מכבה את הסימון הצהוב של פניות שטרם נענו, בלי לענות עליהן ובלי למחוק תשובות
        שהנבחן עדיין לא קרא.
      </Typography>
      <Button
        variant="outlined"
        color="success"
        startIcon={<DoneAllIcon />}
        disabled={pendingIssues === 0}
        onClick={() => run("סימון כל הפניות שטרם נענו כנקראו", userIDs => postBulkMarkAllRead(userIDs))}
      >
        סמן הכל כנקרא ({pendingIssues})
      </Button>
    </Box>
  );
};

/** Pause or resume every targeted user's test. */
const PausePanel = ({ run }) => (
  <Box dir="rtl">
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      עצירה מקפיאה את הטיימר של כל הנבחנים בהיקף שנבחר עד להמשך יזום.
    </Typography>
    <Stack direction="row" spacing={1}>
      <Button
        variant="contained"
        color="error"
        onClick={() => run("עצירת המבחן של כל הנבחנים", userIDs => postBulkSetPaused(true, userIDs))}
      >
        ⏸ עצור לכולם
      </Button>
      <Button
        variant="contained"
        color="success"
        onClick={() => run("המשך המבחן של כל הנבחנים", userIDs => postBulkSetPaused(false, userIDs))}
      >
        ▶ המשך לכולם
      </Button>
    </Stack>
  </Box>
);

/**
 * Global operations: every per-user action, applied to many test-takers at once.
 *
 * @param {() => void} onClose
 * @param {Object[]} allUsers - every active test-taker
 * @param {Object[]} filteredUsers - the users currently matching the operator's filters
 */
const GlobalOperationsDialog = ({ onClose, allUsers, filteredUsers }) => {
  const [scope, setScope] = useState("all");
  const [tab, setTab] = useState(0);
  const [confirm, setConfirm] = useState(null); // { label, action, affected }
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const targetUsers = scope === "filtered" ? filteredUsers : allUsers;
  // An empty list of ids tells the server "everyone"; when scoping to the filtered
  // view we send the exact ids so the two lists can't drift apart.
  const scopedIDs = scope === "filtered" ? filteredUsers.map(u => u.id) : undefined;

  /**
   * Ask for confirmation, then run a bulk action.
   * @param {string} label - what is about to happen, shown in the confirmation
   * @param {(userIDs: string[]|undefined) => Promise<{ok: boolean, data?: Object}>} action
   * @param {number} [affected] - override the affected count (e.g. one questionnaire only)
   */
  const run = (label, action, affected) => {
    setResult(null);
    setConfirm({ label, action, affected: affected ?? targetUsers.length });
  };

  const executeConfirmed = async () => {
    setRunning(true);
    const response = await confirm.action(scopedIDs);
    setRunning(false);
    setConfirm(null);
    if (response.ok) setResult(response.data);
  };

  const panelProps = { targetUsers, run };

  return (
    <>
      <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Typography variant="h6">פעולות גלובליות</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} dir="rtl">
            <Typography variant="body2" sx={{ fontWeight: 600 }}>היקף:</Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={scope}
              onChange={(e, value) => {
                if (!value) return;
                setScope(value);
                setResult(null);
              }}
            >
              <ToggleButton value="all">כל הנבחנים ({allUsers.length})</ToggleButton>
              <ToggleButton value="filtered">לפי הסינון הנוכחי ({filteredUsers.length})</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          <Tabs value={tab} onChange={(e, value) => setTab(value)} variant="scrollable" sx={{ mb: 2 }}>
            <Tab label="הזזה למיקום" />
            <Tab label="זמנים" />
            <Tab label="הודעות" />
            <Tab label="עצירה / המשך" />
          </Tabs>

          {tab === 0 && <MovePanel {...panelProps} />}
          {tab === 1 && <TimePanel {...panelProps} />}
          {tab === 2 && <MessagesPanel {...panelProps} />}
          {tab === 3 && <PausePanel {...panelProps} />}

          <BulkResultPanel result={result} />
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>סגור</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirm} onClose={() => !running && setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle dir="rtl">אישור פעולה גלובלית</DialogTitle>
        <DialogContent>
          <DialogContentText dir="rtl">
            {confirm?.label}
            <br />
            <Typography component="span" variant="caption" color="text.secondary">
              הפעולה תחול על עד {confirm?.affected} נבחנים.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={running}>ביטול</Button>
          <Button onClick={executeConfirmed} variant="contained" color="warning" disabled={running}>
            {running ? <CircularProgress size={20} /> : "אשר"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GlobalOperationsDialog;
